import { isAxiosError } from 'axios';

// Shared error-surfacing contract (G-27, docs/prd/DOD.md §4, story 4-2a; finding
// from the 2026-08 4-1b review round / story 2-125). The SERVER's message is
// the user-facing message: the backend spends real effort writing refusals a
// person can act on ("Preset name already exists", "That replacement template
// does not declare the required field…") into the response body's `message`
// (`backend/src/middlewares/error.js`). Axios's own thrown `Error.message` on
// a failed request is never that — it is the transport's summary of the verb
// and status code ("Request failed with status code 409") — so surfacing IT
// instead throws away everything the server wrote. Six screens each grew
// their own `err.response?.data?.message || err.message || '…'` one-liner (or
// forgot the unwrap entirely) with no shared place the decision lived; this
// is that place. Same class G-22 solved for markup and G-26 for form state.
//
// `errorMessage` is the FLOOR, not a replacement: a caller with a structured
// refusal that already earns bespoke rendering (`revision_conflict`,
// `illegal_transition`, `template_invalid`, `linked_active_types`, quota
// 402s — see `errorReason` below) keeps branching on the response body itself
// and only falls through to this helper for the unhandled case. Never returns
// `stack` — `middlewares/error.js` gating `stack` on `config.env ===
// 'development'` is deliberate dev-only diagnostics, out of scope here.

interface ErrorResponseBody {
  message?: string;
  reason?: string;
}

/** The shape both axios and the agent-plane client expose (story 4-2e). */
interface ResponseCarrier {
  response?: { status?: number; data?: unknown };
}

function carrierOf(err: unknown): ResponseCarrier['response'] | undefined {
  if (isAxiosError(err)) return err.response ?? undefined;
  // The agent plane is a separate service reached by hand-rolled `fetch`
  // (`lib/agentClient.ts` — it is not in the generated client), so its
  // failures are not AxiosErrors. `AgentRequestError` deliberately mirrors
  // axios's `{response: {status, data}}` so ONE unwrap point still serves
  // both planes rather than each screen learning two error shapes (G-27).
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const response = (err as ResponseCarrier).response;
    if (typeof response === 'object' && response !== null) return response;
  }
  return undefined;
}

/** The response body of a failed request, if this is one and it carries one. */
function responseBodyOf(err: unknown): ErrorResponseBody | undefined {
  const data = carrierOf(err)?.data;
  return (data ?? undefined) as ErrorResponseBody | undefined;
}

// The one honest generic fallback (G-27: exactly one, no per-status copy) for
// when the body carries nothing usable — a network failure with no response,
// a non-JSON body, or a body whose `message` isn't a string.
const FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

/**
 * The single unwrap point for a failed mutation's user-facing copy. Prefers
 * the response body's `message`; otherwise the one honest fallback above.
 * Never the transport's own `Error.message`.
 */
export function errorMessage(err: unknown): string {
  const message = responseBodyOf(err)?.message;
  return typeof message === 'string' && message.length > 0 ? message : FALLBACK_MESSAGE;
}

/**
 * The structured `reason` a response body carries, if any — for callers that
 * branch on it instead of (or in addition to) showing the generic sentence,
 * e.g. `illegal_transition`, `duplicate_reference`, quota's own `'quota'`.
 * Returns `undefined` for anything that doesn't carry one, including
 * non-axios errors.
 */
export function errorReason(err: unknown): string | undefined {
  const reason = responseBodyOf(err)?.reason;
  return typeof reason === 'string' ? reason : undefined;
}

/**
 * The HTTP status a failed request came back with, or `undefined` when there
 * was no response at all (a network drop, an aborted request).
 *
 * Added by story 4-2e for P04 §7 ¶4's failure MATRIX: a screen that must tell
 * "the generator is unavailable" (503) from "your plan is full" (402) from
 * "your access changed while you were typing" (403) needs the status, and the
 * alternative was every caller re-deriving `(err as {response}).response
 * ?.status` beside its own `errorMessage()` call. Same single unwrap point,
 * one more fact off the same envelope — the MESSAGE still comes from the
 * server, never from the status (G-27).
 */
export function errorStatus(err: unknown): number | undefined {
  const status = carrierOf(err)?.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * The raw response body, for the callers G-27 explicitly carves out: "the
 * bespoke rendering a structured `reason` already earns". Returns `unknown`
 * on purpose — the caller parses it with the GENERATED contract schema
 * (`lib/api/<module>.gen.ts`), so the shape it renders is the shape the
 * server promised, not a hand-written interface that can drift.
 */
export function errorBody(err: unknown): unknown {
  return carrierOf(err)?.data;
}
